import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { User } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import Card from '../../components/Card';

const mockPatients = [
  {
    id: '1',
    name: 'Mrs. Ngozi Okeke',
    age: 32,
    gender: 'Female',
    condition: 'Pregnant - 24 weeks',
    lastVisit: '2026-01-18',
    nextVisit: '2026-01-25',
    status: 'active',
    phone: '+234 801 234 5678',
  },
  {
    id: '2',
    name: 'Mrs. Fatima Bello',
    age: 28,
    gender: 'Female',
    condition: 'Postnatal Care',
    lastVisit: '2026-01-15',
    nextVisit: '2026-02-01',
    status: 'active',
    phone: '+234 802 345 6789',
  },
  {
    id: '3',
    name: 'Mrs. Adaobi Nwankwo',
    age: 35,
    gender: 'Female',
    condition: 'Pregnant - 12 weeks',
    lastVisit: '2026-01-10',
    nextVisit: '2026-02-05',
    status: 'active',
    phone: '+234 803 456 7890',
  },
  {
    id: '4',
    name: 'Mrs. Aisha Mohammed',
    age: 30,
    gender: 'Female',
    condition: 'Child Health - 18 months',
    lastVisit: '2026-01-05',
    nextVisit: '2026-03-05',
    status: 'active',
    phone: '+234 804 567 8901',
  },
  {
    id: '5',
    name: 'Mrs. Chidinma Eze',
    age: 26,
    gender: 'Female',
    condition: 'Pregnant - 8 weeks',
    lastVisit: '2025-12-20',
    nextVisit: '2026-01-30',
    status: 'active',
    phone: '+234 805 678 9012',
  },
];

const Patients = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedPatient, setSelectedPatient] = useState(null);

  const filteredPatients = mockPatients.filter(patient =>
    patient.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    patient.condition.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="p-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">{t('patientsTitle')}</h1>
        <p className="text-gray-600 mt-1">{t('patientsSubtitle')}</p>
      </div>

      {/* Search */}
      <Card className="p-4 mb-6">
        <input
          type="text"
          placeholder={t('searchPatients')}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
        />
      </Card>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Patient List */}
        <div className="lg:col-span-2">
          <Card className="p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">
              {t('patientList')} ({filteredPatients.length})
            </h2>
            <div className="space-y-3">
              {filteredPatients.map(patient => (
                <div
                  key={patient.id}
                  onClick={() => setSelectedPatient(patient)}
                  className={`p-4 rounded-lg border-2 cursor-pointer transition-colors ${
                    selectedPatient?.id === patient.id
                      ? 'border-primary-500 bg-primary-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h3 className="font-semibold text-gray-900">{patient.name}</h3>
                      <p className="text-sm text-gray-600 mt-1">{patient.condition}</p>
                      <div className="flex items-center space-x-4 mt-2 text-xs text-gray-500">
                        <span>Age: {patient.age}</span>
                        <span>•</span>
                        <span>Last visit: {new Date(patient.lastVisit).toLocaleDateString()}</span>
                      </div>
                    </div>
                    <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium">
                      {patient.status}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        </div>

        {/* Patient Details */}
        <div>
          {selectedPatient ? (
            <Card className="p-6 sticky top-4">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('patientDetails')}</h3>
              
              <div className="space-y-4">
                <div>
                  <label className="text-sm text-gray-600">Name</label>
                  <p className="font-medium text-gray-900">{selectedPatient.name}</p>
                </div>
                
                <div>
                  <label className="text-sm text-gray-600">Age / Gender</label>
                  <p className="font-medium text-gray-900">{selectedPatient.age} years, {selectedPatient.gender}</p>
                </div>
                
                <div>
                  <label className="text-sm text-gray-600">Condition</label>
                  <p className="font-medium text-gray-900">{selectedPatient.condition}</p>
                </div>
                
                <div>
                  <label className="text-sm text-gray-600">Phone</label>
                  <p className="font-medium text-gray-900">{selectedPatient.phone}</p>
                </div>
                
                <div>
                  <label className="text-sm text-gray-600">Last Visit</label>
                  <p className="font-medium text-gray-900">
                    {new Date(selectedPatient.lastVisit).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                    })}
                  </p>
                </div>
                
                <div>
                  <label className="text-sm text-gray-600">Next Visit</label>
                  <p className="font-medium text-gray-900">
                    {new Date(selectedPatient.nextVisit).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                    })}
                  </p>
                </div>
              </div>

              <div className="mt-6 pt-6 border-t border-gray-200 space-y-2">
                <button 
                  onClick={() => navigate(`/doctor/patient-record/${selectedPatient.id}`)}
                  className="w-full bg-primary-600 text-white py-2 rounded-lg hover:bg-primary-700 transition-colors"
                >
                  View Full Record
                </button>
                <button 
                  onClick={() => navigate(`/doctor/schedule-appointment/${selectedPatient.id}`)}
                  className="w-full bg-gray-100 text-gray-700 py-2 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  Schedule Appointment
                </button>
              </div>
            </Card>
          ) : (
            <Card className="p-6">
              <div className="text-center text-gray-500">
                <div className="mb-3 flex justify-center"><User className="w-12 h-12 text-gray-400" /></div>
                <p>Select a patient to view details</p>
              </div>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};

export default Patients;
